/**
 * Shared photo validation utilities
 *
 * Client-side preflight checks for photo uploads (type, size).
 * Used by both profile photo and check-in photo upload flows.
 */

import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
}

/**
 * Validate a photo before upload (client-side preflight check).
 * Checks MIME type and file size.
 */
export async function validatePhotoBeforeUpload(
  uri: string,
  mimeType?: string,
  platform: string = Platform.OS
): Promise<{ valid: boolean; error?: string }> {
  // Validate MIME type
  const type = mimeType || 'image/jpeg';
  if (!ALLOWED_IMAGE_TYPES.includes(type.toLowerCase())) {
    return {
      valid: false,
      error: `Invalid file type: ${type}. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`,
    };
  }

  // Get file size based on platform
  let fileSize: number;
  if (platform === 'web' && uri.startsWith('blob:')) {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();
      fileSize = blob.size;
    } catch {
      return {
        valid: false,
        error: 'Failed to read file size. Please try again.',
      };
    }
  } else {
    try {
      const fileInfo = await FileSystem.getInfoAsync(uri);
      if (!fileInfo.exists) {
        return {
          valid: false,
          error: 'File not found. Please try selecting the image again.',
        };
      }
      fileSize = fileInfo.size || 0;
    } catch {
      return {
        valid: false,
        error: 'Failed to read file size. Please try again.',
      };
    }
  }

  // Validate file size
  if (fileSize > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `Photo is ${formatFileSize(fileSize)}; max is ${formatFileSize(MAX_FILE_SIZE)}`,
    };
  }

  return { valid: true };
}
